/**
 * Decodes a packed point cloud — one fixed-size record per point — into the
 * vertex arrays the 3D panel uploads to Babylon.
 *
 * Deliberately neutral about where the layout came from: `foxglove.PointCloud`
 * is one adapter away (`foxgloveMessageAdapter.ts`), and `sensor_msgs/PointCloud2`
 * would be another. Nothing in this module is Babylon-specific, so it runs in
 * the data-server worker.
 */

/**
 * The numeric type of one packed field, numbered as `foxglove.NumericType` is.
 * Integers are little-endian. `sensor_msgs/PointCloud2` numbers the integer
 * types differently, so an adapter for it has to translate before reaching here.
 */
export enum NumericType {
  UNKNOWN = 0,
  UINT8 = 1,
  INT8 = 2,
  UINT16 = 3,
  INT16 = 4,
  UINT32 = 5,
  INT32 = 6,
  FLOAT32 = 7,
  FLOAT64 = 8,
}

/** One field of a point record: where in the record it sits, and how to read it. */
export interface PackedField {
  name: string;
  /** Byte offset from the start of each point's record. */
  offset: number;
  type: NumericType;
}

/** A cloud's bytes with the layout that interprets them. */
export interface PackedPointLayout {
  /** Bytes between consecutive points. */
  stride: number;
  fields: PackedField[];
  data: Uint8Array;
}

/** The cloud's origin within its frame of reference: `p' = R(orientation) · p + position`. */
export interface PointCloudPose {
  position: { x: number; y: number; z: number };
  orientation: { x: number; y: number; z: number; w: number };
}

/** Which source coordinate feeds one of the scene's axes, and with what sign/scale. */
export interface PointCloudAxisMapping {
  field: "x" | "y" | "z";
  scale: number;
}

/** How source coordinates become scene coordinates, and which points to keep. */
export interface PointCloudMapping {
  right: PointCloudAxisMapping;
  forward: PointCloudAxisMapping;
  up: PointCloudAxisMapping;
  /** Keep every Nth point; 1 keeps them all. */
  stride: number;
}

/** The decoded points of one message, before the reader stamps its log time on. */
export interface DecodedPointCloud {
  /**
   * Number of points after dropping non-finite coordinates and applying stride-based decimation.
   */
  pointCount: number;
  /**
   * Babylon `(x = right, y = up, z = forward)` triples, relative to
   * `parentTranslation`.
   */
  positions: Float32Array;
  /**
   * RGBA bytes, 4 per point, 0 to 255. Absent when the message carries no
   * red/green/blue fields. Uploaded as a normalized byte attribute, which the
   * shader reads as `[0, 1]`: 4 bytes a point where floats would be 16, for
   * no visible difference on an 8-bit framebuffer.
   */
  colors?: Uint8Array;
  /**
   * The translation of the vertices' parent node, in scene axes: the first
   * kept point, which every vertex is stored relative to. Its purpose is the
   * GPU, not the recorded precision, which is already the source's: the
   * vertex shader composes a vertex with its node's world matrix in float32,
   * so a cloud recorded in a map frame hundreds of kilometres from zero would
   * swim by centimetres as the camera moved if its vertices carried those
   * coordinates. Taken off here in float64, it leaves the vertices within the
   * cloud's own extent, and the ECS composes it with the entity's global
   * position in float64 before the result reaches Babylon.
   */
  parentTranslation: { x: number; y: number; z: number };
  /**
   * The cloud's local origin: the point it is centered on, the middle of its
   * kept points' bounding box, in scene axes and in the same frame as
   * `parentTranslation`. Every gradient measures from it, distance and axes
   * alike. The frame's origin for an empty cloud.
   */
  origin: { x: number; y: number; z: number };
  /**
   * Per point, its height and its distance from `origin`, each as a `uint16`
   * fraction `0..65535` of that measure's `span` in `measureSpans`, height
   * first. These are a gradient's lookups into its colors: uploaded as a
   * normalized `uv` attribute, the GPU colors the frame by reading a strip of
   * the gradient's colors at each point's lookup, so no per-point work runs on
   * the main thread, and a gradient edit only redraws the strip or moves it.
   * 4 bytes a point.
   */
  measures: Uint16Array;
  /** What the `measures` stand for, per gradient measure. */
  measureSpans: PointCloudMeasureSpans;
}

/** A measure's low and high value. */
export interface MeasureSpan {
  min: number;
  max: number;
}

/** How a frame's `measures` read for one gradient measure. */
export interface MeasureReading {
  /** The values a lookup of 0 and of 65535 stand for. */
  span: MeasureSpan;
  /**
   * The frame's own lowest and highest value: a gradient's range at an end it
   * leaves unpinned.
   */
  extremes: MeasureSpan;
}

/** The two gradient measures a frame is read by, from its `origin`. */
export interface PointCloudMeasureSpans {
  up: MeasureReading;
  distance: MeasureReading;
}

/** The readings of a frame with no points: every span and extreme at zero. */
export const ZERO_MEASURE_SPANS: PointCloudMeasureSpans = Object.freeze({
  up: { span: { min: 0, max: 0 }, extremes: { min: 0, max: 0 } },
  distance: { span: { min: 0, max: 0 }, extremes: { min: 0, max: 0 } },
});

/** The highest lookup, a `uint16`'s maximum. */
export const MEASURE_MAX = 0xffff;

/** One decoded message, as the worker hands it back. */
export interface PointCloudFrame extends DecodedPointCloud {
  logTime: bigint;
}

const BYTE_SIZE: Record<Exclude<NumericType, NumericType.UNKNOWN>, number> = {
  [NumericType.UINT8]: 1,
  [NumericType.INT8]: 1,
  [NumericType.UINT16]: 2,
  [NumericType.INT16]: 2,
  [NumericType.UINT32]: 4,
  [NumericType.INT32]: 4,
  [NumericType.FLOAT32]: 4,
  [NumericType.FLOAT64]: 8,
};

/**
 * What an integer channel's maximum normalises to 1.0; float channels are taken
 * as already in `[0, 1]`.
 */
const COLOR_DIVISOR: Record<
  Exclude<NumericType, NumericType.UNKNOWN>,
  number
> = {
  [NumericType.UINT8]: 0xff,
  [NumericType.INT8]: 0x7f,
  [NumericType.UINT16]: 0xffff,
  [NumericType.INT16]: 0x7fff,
  [NumericType.UINT32]: 0xffffffff,
  [NumericType.INT32]: 0x7fffffff,
  [NumericType.FLOAT32]: 1,
  [NumericType.FLOAT64]: 1,
};

type FieldReader = (view: DataView, byteOffset: number) => number;

/** A little-endian reader for one numeric type. */
export function readerFor(type: NumericType): FieldReader {
  switch (type) {
    case NumericType.UINT8:
      return (view, offset) => view.getUint8(offset);
    case NumericType.INT8:
      return (view, offset) => view.getInt8(offset);
    case NumericType.UINT16:
      return (view, offset) => view.getUint16(offset, true);
    case NumericType.INT16:
      return (view, offset) => view.getInt16(offset, true);
    case NumericType.UINT32:
      return (view, offset) => view.getUint32(offset, true);
    case NumericType.INT32:
      return (view, offset) => view.getInt32(offset, true);
    case NumericType.FLOAT32:
      return (view, offset) => view.getFloat32(offset, true);
    case NumericType.FLOAT64:
      return (view, offset) => view.getFloat64(offset, true);
    case NumericType.UNKNOWN:
      throw new Error("Cannot read a point cloud field of type UNKNOWN");
  }
}

/** A field's reader, checked to fit inside the point record. */
function boundReader(field: PackedField, stride: number): FieldReader {
  if (field.type === NumericType.UNKNOWN) {
    throw new Error(
      `Point cloud field '${field.name}' has type UNKNOWN and cannot be read`,
    );
  }
  const end = field.offset + BYTE_SIZE[field.type];
  if (end > stride) {
    throw new Error(
      `Point cloud field '${field.name}' ends at byte ${end}, past the point stride of ${stride}`,
    );
  }
  const read = readerFor(field.type);
  const offset = field.offset;
  return (view, base) => read(view, base + offset);
}

/**
 * Reads one color channel as a byte, 0 to 255: a `UINT8` channel as it is, the
 * common case and the fast one; any other integer scaled by its type's maximum
 * and a float by 1, clamped to `[0, 1]` and rounded to the nearest byte.
 */
function colorReader(field: PackedField, stride: number): FieldReader {
  const read = boundReader(field, stride);
  if (field.type === NumericType.UINT8) {
    return read;
  }
  const divisor =
    field.type === NumericType.UNKNOWN ? 1 : COLOR_DIVISOR[field.type];
  return (view, base) => {
    const unit = Math.min(1, Math.max(0, read(view, base) / divisor));
    return (unit * 255 + 0.5) | 0;
  };
}

const ZERO: FieldReader = () => 0;
const OPAQUE: FieldReader = () => 255;

/** The row-major 3×3 rotation matrix of a quaternion, identity for a zero quaternion. */
function rotationMatrixOf(orientation: PointCloudPose["orientation"]) {
  const { x, y, z, w } = orientation;
  const norm = Math.hypot(x, y, z, w);
  if (!(norm > 0) || !Number.isFinite(norm)) {
    // Some publishers send an all-zero quaternion for "no orientation".
    return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  }
  const qx = x / norm;
  const qy = y / norm;
  const qz = z / norm;
  const qw = w / norm;
  return [
    1 - 2 * (qy * qy + qz * qz),
    2 * (qx * qy - qz * qw),
    2 * (qx * qz + qy * qw),
    2 * (qx * qy + qz * qw),
    1 - 2 * (qx * qx + qz * qz),
    2 * (qy * qz - qx * qw),
    2 * (qx * qz - qy * qw),
    2 * (qy * qz + qx * qw),
    1 - 2 * (qx * qx + qy * qy),
  ];
}

const SOURCE_AXIS_INDEX = { x: 0, y: 1, z: 2 } as const;

/**
 * Decodes a packed cloud into scene-space vertices.
 *
 * Every point is read by `(type, offset)` through a little-endian `DataView`,
 * posed in the source frame, mapped onto the scene's right/forward/up axes, and
 * stored relative to the first kept point, the `parentTranslation`. A point
 * with a non-finite coordinate
 * is dropped — lidar drivers encode "no return" as `NaN`, and one `NaN` vertex
 * poisons Babylon's bounding box. A trailing partial record is ignored.
 *
 * Colors are produced only when the layout has all of `red`, `green` and
 * `blue`, as bytes; `alpha` is optional and reads as 255 when absent.
 *
 * @throws when the layout cannot be read: a stride of zero, fewer than two
 *   coordinate fields, a coordinate or color field of `UNKNOWN` type, or a used
 *   field that runs past the point stride.
 */
export function decodePackedPointCloud(
  layout: PackedPointLayout,
  pose: PointCloudPose,
  mapping: PointCloudMapping,
): DecodedPointCloud {
  const { stride, fields, data } = layout;
  if (!Number.isInteger(stride) || stride <= 0) {
    throw new Error(`Point cloud has an invalid point stride of ${stride}`);
  }
  const byName = new Map(fields.map((field) => [field.name, field]));

  const coordinateFields = ["x", "y", "z"].filter((name) => byName.has(name));
  if (coordinateFields.length < 2) {
    throw new Error(
      `Point cloud needs at least two of the x, y and z fields; it has ${coordinateFields.length}`,
    );
  }
  const readCoordinate = (name: string): FieldReader => {
    const field = byName.get(name);
    return field ? boundReader(field, stride) : ZERO;
  };
  const readX = readCoordinate("x");
  const readY = readCoordinate("y");
  const readZ = readCoordinate("z");

  const red = byName.get("red");
  const green = byName.get("green");
  const blue = byName.get("blue");
  const alpha = byName.get("alpha");
  const readColors =
    red && green && blue
      ? {
          red: colorReader(red, stride),
          green: colorReader(green, stride),
          blue: colorReader(blue, stride),
          alpha: alpha ? colorReader(alpha, stride) : OPAQUE,
        }
      : undefined;

  const [r00, r01, r02, r10, r11, r12, r20, r21, r22] = rotationMatrixOf(
    pose.orientation,
  );
  const { x: tx, y: ty, z: tz } = pose.position;

  const rightIndex = SOURCE_AXIS_INDEX[mapping.right.field];
  const upIndex = SOURCE_AXIS_INDEX[mapping.up.field];
  const forwardIndex = SOURCE_AXIS_INDEX[mapping.forward.field];
  const rightScale = mapping.right.scale;
  const upScale = mapping.up.scale;
  const forwardScale = mapping.forward.scale;

  const keepEvery = Math.max(1, Math.trunc(mapping.stride));
  const recordCount = Math.floor(data.byteLength / stride);
  const maxKept = Math.ceil(recordCount / keepEvery);
  const positions = new Float32Array(maxKept * 3);
  const colors = readColors ? new Uint8Array(maxKept * 4) : undefined;
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const posed = [0, 0, 0];
  // The kept points' bounding box, in scene axes, for the local origin.
  let minRight = Infinity;
  let minUp = Infinity;
  let minForward = Infinity;
  let maxRight = -Infinity;
  let maxUp = -Infinity;
  let maxForward = -Infinity;

  let kept = 0;
  let parentTranslation: DecodedPointCloud["parentTranslation"] | undefined;
  for (let i = 0; i < recordCount; i += keepEvery) {
    const base = i * stride;
    const sx = readX(view, base);
    const sy = readY(view, base);
    const sz = readZ(view, base);
    if (!Number.isFinite(sx) || !Number.isFinite(sy) || !Number.isFinite(sz)) {
      continue;
    }
    // The pose applies in the source frame, before any axis mapping.
    posed[0] = r00 * sx + r01 * sy + r02 * sz + tx;
    posed[1] = r10 * sx + r11 * sy + r12 * sz + ty;
    posed[2] = r20 * sx + r21 * sy + r22 * sz + tz;
    // `+ 0` turns the `-0` a negative scale makes of a zero coordinate back
    // into `0`, so vertices and translation serialize and compare as plain
    // numbers.
    const right = posed[rightIndex] * rightScale + 0;
    const up = posed[upIndex] * upScale + 0;
    const forward = posed[forwardIndex] * forwardScale + 0;
    parentTranslation ??= { x: right, y: up, z: forward };
    minRight = Math.min(minRight, right);
    minUp = Math.min(minUp, up);
    minForward = Math.min(minForward, forward);
    maxRight = Math.max(maxRight, right);
    maxUp = Math.max(maxUp, up);
    maxForward = Math.max(maxForward, forward);
    positions[kept * 3] = right - parentTranslation.x;
    positions[kept * 3 + 1] = up - parentTranslation.y;
    positions[kept * 3 + 2] = forward - parentTranslation.z;
    if (colors && readColors) {
      colors[kept * 4] = readColors.red(view, base);
      colors[kept * 4 + 1] = readColors.green(view, base);
      colors[kept * 4 + 2] = readColors.blue(view, base);
      colors[kept * 4 + 3] = readColors.alpha(view, base);
    }
    kept++;
  }
  // `+ 0` as for the vertices: the midpoint of two -0s is -0.
  const origin =
    kept === 0
      ? { x: 0, y: 0, z: 0 }
      : {
          x: (minRight + maxRight) / 2 + 0,
          y: (minUp + maxUp) / 2 + 0,
          z: (minForward + maxForward) / 2 + 0,
        };
  const translation = parentTranslation ?? { x: 0, y: 0, z: 0 };

  return {
    pointCount: kept,
    // Trimmed to the kept points so the buffer handed to Babylon (and across
    // the worker boundary) is exactly the vertex data, with no slack.
    positions: kept === maxKept ? positions : positions.slice(0, kept * 3),
    colors: colors && (kept === maxKept ? colors : colors.slice(0, kept * 4)),
    parentTranslation: translation,
    origin,
    ...measure(positions, kept, translation, origin, {
      halfUp: (maxUp - minUp) / 2,
      halfDiagonal: Math.hypot(
        (maxRight - minRight) / 2,
        (maxUp - minUp) / 2,
        (maxForward - minForward) / 2,
      ),
    }),
  };
}

/**
 * Reads every kept point's height and distance from `origin` into `uint16`
 * lookups, and says what they span.
 *
 * Height spans the box: its lowest point reads 0, its highest 65535, and the
 * origin, the box's middle, half way. Distance is referred to the box's half
 * diagonal, the farthest from the middle any point inside the box can be, so
 * the reference is known before the pass; the frame's own nearest and
 * farthest points, the automatic range, are found on the way. A box with no
 * height, or no size, reads 0 throughout.
 */
function measure(
  positions: Float32Array,
  kept: number,
  parentTranslation: { x: number; y: number; z: number },
  origin: { x: number; y: number; z: number },
  box: { halfUp: number; halfDiagonal: number },
): Pick<DecodedPointCloud, "measures" | "measureSpans"> {
  const measures = new Uint16Array(kept * 2);
  if (kept === 0) {
    return { measures, measureSpans: ZERO_MEASURE_SPANS };
  }
  const { halfUp, halfDiagonal } = box;
  // Each vertex relative to the origin rather than to the vertices' own base.
  const baseX = parentTranslation.x - origin.x;
  const baseY = parentTranslation.y - origin.y;
  const baseZ = parentTranslation.z - origin.z;
  const upScale = halfUp > 0 ? MEASURE_MAX / (2 * halfUp) : 0;
  const distanceScale = halfDiagonal > 0 ? MEASURE_MAX / halfDiagonal : 0;
  let nearest = Infinity;
  let farthest = -Infinity;
  for (let i = 0; i < kept; i++) {
    const x = positions[i * 3] + baseX;
    const y = positions[i * 3 + 1] + baseY;
    const z = positions[i * 3 + 2] + baseZ;
    const distance = Math.sqrt(x * x + y * y + z * z);
    nearest = Math.min(nearest, distance);
    farthest = Math.max(farthest, distance);
    // Clamped: float32 vertices can land a hair outside the float64 box.
    measures[i * 2] = Math.min(
      MEASURE_MAX,
      Math.max(0, (y + halfUp) * upScale + 0.5),
    );
    measures[i * 2 + 1] = Math.min(MEASURE_MAX, distance * distanceScale + 0.5);
  }
  const up = { min: -halfUp + 0, max: halfUp };
  return {
    measures,
    measureSpans: {
      up: { span: up, extremes: up },
      distance: {
        span: { min: 0, max: halfDiagonal },
        extremes: { min: nearest, max: farthest },
      },
    },
  };
}
